import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, DollarSign, Calendar, Store, UserCheck, XCircle } from 'lucide-react'
import { adminStatsService, adminVendorsService } from '../services'
import { formatPriceDisplay } from '../utils/formatPrice'
import './Dashboard.css'

const Dashboard = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState(null)
  const [error, setError] = useState(null)
  const [pendingVendors, setPendingVendors] = useState([])
  const [pendingActionId, setPendingActionId] = useState(null)

  const loadPendingVendors = useCallback(async () => {
    const list = await adminVendorsService.list({ accountStatus: 'pending' })
    setPendingVendors(list || [])
  }, [])

  const approveVendor = async (id) => {
    setPendingActionId(id)
    try {
      await adminVendorsService.updateStatus(id, 'approved')
      await loadPendingVendors()
      const statsRes = await adminStatsService.getStats()
      setStats(statsRes)
    } catch (e) {
      console.error(e)
      window.alert(e.response?.data?.error || e.message || 'Failed to approve')
    } finally {
      setPendingActionId(null)
    }
  }

  const rejectVendor = async (id) => {
    if (!window.confirm('Reject this application? The partner will not be able to use the app.')) {
      return
    }
    setPendingActionId(id)
    try {
      await adminVendorsService.updateStatus(id, 'rejected')
      await loadPendingVendors()
      const statsRes = await adminStatsService.getStats()
      setStats(statsRes)
    } catch (e) {
      console.error(e)
      window.alert(e.response?.data?.error || e.message || 'Failed to reject')
    } finally {
      setPendingActionId(null)
    }
  }

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const [statsRes, pendingList] = await Promise.all([
          adminStatsService.getStats(),
          adminVendorsService.list({ accountStatus: 'pending' }),
        ])
        if (cancelled) return
        setStats(statsRes)
        setPendingVendors(pendingList || [])
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.error || e.message || 'Failed to load dashboard')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  if (loading) {
    return (
      <div className="dashboard">
        <div className="page-header">
          <h1>Dashboard Overview</h1>
          <p>Loading...</p>
        </div>
        <div className="stats-grid" style={{ opacity: 0.6 }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="stat-card" style={{ minHeight: 100 }} />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="page-header">
          <h1>Dashboard Overview</h1>
          <p style={{ color: 'var(--danger-color, #ef4444)' }}>{error}</p>
        </div>
      </div>
    )
  }

  const s = stats || {}
  const pendingCount = s.pendingVendorApprovals ?? pendingVendors.length
  const statCards = [
    { title: 'Total Users', value: String(s.totalUsers ?? 0), icon: Users, color: '#02c0a7' },
    { title: 'Active Partners', value: `${s.activeVendors ?? 0}/${s.totalVendors ?? 0}`, icon: Store, color: '#10b981' },
    { title: 'Published Plans', value: String(s.publishedPlans ?? 0), icon: Calendar, color: '#8b5cf6' },
    { title: 'Revenue', value: `${formatPriceDisplay(s.totalRevenue ?? 0)} EGP`, icon: DollarSign, color: '#f59e0b' },
  ]

  return (
    <div className="dashboard">
      <div className="page-header">
        <div className="header-titles">
          <h1>Dashboard Overview</h1>
          <p>Welcome back! Here&apos;s what&apos;s happening with IN today.</p>
        </div>
      </div>

      <div className="stats-grid">
        {statCards.map((stat, index) => {
          const Icon = stat.icon
          return (
            <div key={index} className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: `${stat.color}15` }}>
                <Icon size={24} style={{ color: stat.color }} />
              </div>
              <div className="stat-content">
                <p className="stat-title">{stat.title}</p>
                <h3 className="stat-value">{stat.value}</h3>
              </div>
            </div>
          )
        })}
      </div>

      <section
        className="dashboard-pending-approvals"
        style={{ marginTop: '2.5rem' }}
      >
        <div
          className="page-header"
          style={{ marginBottom: '1rem' }}
        >
          <h2
            style={{
              fontSize: '1.25rem',
              fontWeight: 700,
              color: 'var(--text-primary)',
            }}
          >
            Partner applications pending review
            {pendingCount > 0 && (
              <span
                style={{
                  marginLeft: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                }}
              >
                (
                {pendingCount}
                )
              </span>
            )}
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            Approve or reject new partner sign-ups before they can use the mobile app.
          </p>
        </div>
        {pendingVendors.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            No applications waiting for review.
          </p>
        ) : (
          <div
            className="pending-vendors-list"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
            }}
          >
            {pendingVendors.map((v) => (
              <div
                key={v.id}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                  background: 'var(--surface)',
                  borderRadius: '0.75rem',
                  padding: '1rem 1.25rem',
                  boxShadow: '0 1px 3px 0 rgba(0,0,0,0.08)',
                }}
              >
                <div style={{ flex: '1 1 200px' }}>
                  <p style={{ fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>
                    {v.name || v.businessName || 'Partner'}
                  </p>
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {v.userEmail || v.user_email || v.email}
                    {v.userPhone || v.user_phone
                      ? ` · ${v.userPhone || v.user_phone}`
                      : ''}
                  </p>
                  <p style={{ margin: '0.35rem 0 0', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    ID: {v.id}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      padding: '0.5rem 0.9rem',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: pendingActionId ? 'not-allowed' : 'pointer',
                      background: '#10b981',
                      color: '#fff',
                    }}
                    disabled={!!pendingActionId}
                    onClick={() => approveVendor(v.id)}
                  >
                    <UserCheck size={18} />
                    Approve
                  </button>
                  <button
                    type="button"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      padding: '0.5rem 0.9rem',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      border: '1px solid var(--border, #e5e7eb)',
                      borderRadius: '0.5rem',
                      cursor: pendingActionId ? 'not-allowed' : 'pointer',
                      background: 'var(--surface)',
                      color: 'var(--text-primary)',
                    }}
                    disabled={!!pendingActionId}
                    onClick={() => rejectVendor(v.id)}
                  >
                    <XCircle size={18} />
                    Reject
                  </button>
                  <button
                    type="button"
                    style={{
                      padding: '0.5rem 0.9rem',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      border: 'none',
                      background: 'transparent',
                      color: '#02c0a7',
                      cursor: 'pointer',
                    }}
                    onClick={() => navigate(`/vendors/${v.id}`)}
                  >
                    Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

export default Dashboard
